import { ChangeEvent, useMemo } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { DataTableFilter, useInvoicesDataTable } from 'generated/resources/datatable.graphql'
import {
  AvailableCurrencyFragment,
  ContactFragment,
  FinanceFinancialDocumentFilter,
} from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import useSearchParams from 'modules/core/hooks/useSearchParams'
import i18n from 'modules/core/lib/i18n'

import { FinancialDocumentInsights } from 'modules/finance/components/FinancialDocumentInsights'
import { InvoiceFilters } from 'modules/finance/components/Invoices/DataTableWrapper/types'

import { DataTableSendToPayBulkAction } from './DataTableSendToPayBulkAction'
import InvoiceTabs from './InvoiceTabs'
import { currencyFilter } from './currencyFilter'
import { dateFilter, dueDateFilter } from './dateRangeFilters'
import { documentTypeFilter, legalEntityFilter, validatedFilter } from './filters'
import { projectFilter } from './projectFilter'
import { statusFilter } from './statusFilter'
import { vendorFilter } from './vendorFilter'

interface Props {
  source: ReturnType<typeof useInvoicesDataTable>
  onFilterChange: (filter: InvoiceFilters) => void
  search: string
  onSearch: (event: ChangeEvent<HTMLInputElement>) => void
  onTabChange: (tab: string) => void
  tab: string
  filters: InvoiceFilters
  legalEntitiesOptions: { value: number; label: string }[]
  financialDocumentsPaymentsAvailable: boolean
  onUploadClick?: () => void
  onExportClick: () => void
  currency: string
  locale: string
  vendor?: ContactFragment
  availableCurrencies: AvailableCurrencyFragment[]
}

const InvoicesDataTable = ({
  source,
  onFilterChange,
  search,
  onSearch,
  onTabChange,
  tab,
  filters,
  legalEntitiesOptions,
  financialDocumentsPaymentsAvailable,
  onUploadClick,
  onExportClick,
  currency,
  availableCurrencies,
  locale,
  vendor,
}: Props) => {
  const canSendToPay = financialDocumentsPaymentsAvailable
  const {
    validated,
    currency: currencySearchParamValue,
    fromDueDate: fromDueDateSearchParamValue,
    toDueDate: toDueDateSearchParamValue,
  } = useSearchParams<{
    validated?: boolean
    currency?: string
    fromDueDate?: string
    toDueDate?: string
  }>()

  const {
    company: { id: companyId },
  } = useCurrentSession()

  const dataTableFilters: DataTableFilter<FinanceFinancialDocumentFilter>[] = useMemo(
    () => [
      statusFilter(onFilterChange, tab, filters.status),
      dateFilter(onFilterChange, filters.fromDate, filters.toDate),
      legalEntityFilter(onFilterChange, legalEntitiesOptions, filters.legalEntity),
      dueDateFilter(
        onFilterChange,
        filters.fromDueDate ||
          (fromDueDateSearchParamValue ? new Date(fromDueDateSearchParamValue) : undefined),
        filters.toDueDate ||
          (toDueDateSearchParamValue ? new Date(toDueDateSearchParamValue) : undefined)
      ),
      validatedFilter(onFilterChange, filters.validated ?? validated),
      documentTypeFilter(onFilterChange, filters.documentType),
      currencyFilter(
        onFilterChange,
        availableCurrencies || [],
        filters.currency || (currencySearchParamValue ? [currencySearchParamValue] : undefined)
      ),
      projectFilter(onFilterChange, filters.projectIds),
      vendorFilter(onFilterChange, filters.vendorIds),
    ],
    [
      onFilterChange,
      tab,
      filters.status,
      filters.fromDate,
      filters.toDate,
      filters.legalEntity,
      legalEntitiesOptions,
      filters.fromDueDate,
      filters.toDueDate,
      filters.validated,
      validated,
      filters.documentType,
      filters.currency,
      availableCurrencies,
      currencySearchParamValue,
      filters.projectIds,
      fromDueDateSearchParamValue,
      toDueDateSearchParamValue,
    ]
  )

  return (
    <>
      {!vendor && (
        <InvoiceTabs
          currentTab={tab}
          onTabChange={onTabChange}
          financialDocumentsPaymentsAvailable={financialDocumentsPaymentsAvailable}
        />
      )}
      <DataTable.Provider
        key={`invoices_table_${tab}`}
        source={source}
        persistFilters={false}
        pagination={{
          kind: 'offset',
          itemsPerPage: 25,
        }}
        sort={{ path: 'createdAt', direction: 'desc' }}
        filters={dataTableFilters}
      >
        <Box gap="s16">
          <DataTable.HeadBar
            disableExport
            search={{
              placeholder: i18n.t('common.search'),
              value: search,
              onChange: onSearch,
            }}
            actions={
              <>
                {onUploadClick && (
                  <Button onClick={onUploadClick}>
                    {i18n.t('finance.invoices.actions.upload_invoices')}
                  </Button>
                )}
                {canSendToPay && <DataTableSendToPayBulkAction />}
              </>
            }
            customPopoverOptions={
              <PopoverMenu.TouchableOption
                key="export"
                title={i18n.t('common.export')}
                onClick={onExportClick}
              />
            }
          />
          {!vendor && (
            <FinancialDocumentInsights
              companyId={companyId}
              currency={currency}
              locale={locale}
              canSendToPay={canSendToPay}
            />
          )}
          <DataTable.Table selectable={() => canSendToPay} />
        </Box>
      </DataTable.Provider>
    </>
  )
}

export default InvoicesDataTable
